/**
 * Submit hook for the unified `IntakeInteractionForm` (Clinical / CoCM /
 * Unbilled intake). Handles:
 *  - scribe encounter ID-load on submit (when `_scribeEncounterId` is set)
 *  - upsert via the encounter facade
 *  - the `complete-intake` activity completion + enrollment chain
 *  - draft save (skips activity completion / chain)
 *
 * The persisted encounter-type code is taken from `formData["encounter-type"]`,
 * which the form derives from the Schedule Intake billing flag (new) or the
 * existing encounter's tag (edit).
 *
 * @agentic connections
 */

import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  Encounter,
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";
import type {
  EncounterActivityContext,
  IntakeInteractionFormData,
} from "@benchmark/core/types";
import { createEncounterFacade } from "@/facades/encounter.facade";
import { reconcileIntakeChargeItem } from "@benchmark/core/actions";
import { toBundleInput } from "@benchmark/core/adapters";
import { fromBundleInput } from "@benchmark/core/adapters";
import { encounterToForm } from "@benchmark/core/adapters";
import { loadEncounterFormDataContext } from "@benchmark/core/services";
import { buildDraftFormStateRecord } from "@benchmark/core/adapters";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "@benchmark/ui/utils";
import { useCompleteIntakeChain } from "../activity-chain/useCompleteIntakeChain";
import { getMissingRequiredIntakeSections } from "./intake-required-sections";

/**
 * Options for {@link useIntakeInteractionSubmit}.
 * @agentic connections
 */
export type UseIntakeInteractionSubmitOptions = {
  patient: Patient | undefined;
  existingEncounter?: Encounter;
  originalFormData?: IntakeInteractionFormData;
  setOriginalFormData?: (data: IntakeInteractionFormData) => void;
  practitionerRoles?: PractitionerRole[];
  activityContext?: EncounterActivityContext;
  onSuccess?: (encounterId: string) => void;
  onSubmitted?: () => void;
  discardDraft?: () => void;
};

/**
 * Result API for the unified intake submit hook: finalize + draft handlers.
 * @agentic connections
 */
export type UseIntakeInteractionSubmitResult = {
  handleSubmit: (formData: IntakeInteractionFormData) => Promise<void>;
  handleDraftSave: (formData: IntakeInteractionFormData) => Promise<void>;
};

/** Whether current answers would overwrite an unseen QR on a Scribe encounter. */
export const hasScribeQuestionnaireConflict = (
  currentResponses: IntakeInteractionFormData["questionnaireResponses"],
  currentIds: IntakeInteractionFormData["_questionnaireResponseIds"],
  loadedIds: IntakeInteractionFormData["_questionnaireResponseIds"],
): boolean =>
  Object.entries(currentResponses ?? {}).some(
    ([url, entry]) =>
      entry.responses.length > 0 &&
      Boolean(loadedIds?.[url]) &&
      currentIds?.[url] !== loadedIds?.[url],
  );

/**
 * Returns finalize + draft submit handlers for `IntakeInteractionForm`.
 * @agentic connections
 */
export const useIntakeInteractionSubmit = ({
  patient,
  existingEncounter,
  originalFormData,
  setOriginalFormData,
  practitionerRoles,
  activityContext,
  onSuccess,
  onSubmitted,
  discardDraft,
}: UseIntakeInteractionSubmitOptions): UseIntakeInteractionSubmitResult => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const navigate = useNavigate();
  const intakeChain = useCompleteIntakeChain({
    patientId: patient?.id,
    carePlanId: activityContext?.carePlanId,
  });

  const handleSubmit = useCallback(
    async (formData: IntakeInteractionFormData) => {
      if (!profile || !patient) {
        showErrorNotification(
          "Missing required data. Please refresh the page and try again.",
        );
        return;
      }

      // Submit-only required-section gate (FR-D6.1 / D21): block final submit
      // with a list of missing sections. This runs only here (the final-submit
      // path), never on step navigation, so prose is never validated mid-call.
      const missingSections = getMissingRequiredIntakeSections(formData);
      if (missingSections.length > 0) {
        showErrorNotification(
          `Complete these required sections before submitting: ${missingSections.join(", ")}.`,
          { autoClose: 8000 },
        );
        return;
      }

      const intakeType = formData["encounter-type"];

      try {
        const myRole = (practitionerRoles ?? []).find(
          (role) =>
            role.practitioner?.reference === `Practitioner/${profile.id}`,
        );

        let encounterToUpdate = existingEncounter;
        let updatedOriginalFormData = originalFormData;

        if (!encounterToUpdate && formData._scribeEncounterId) {
          try {
            encounterToUpdate = await medplum.readResource(
              "Encounter",
              formData._scribeEncounterId,
            );
            const context = await loadEncounterFormDataContext(
              medplum,
              encounterToUpdate,
              patient,
              { throwOnError: true },
            );
            const loadedFlat = encounterToForm(context);
            const loadedTyped = fromBundleInput(intakeType, loadedFlat);
            if (
              hasScribeQuestionnaireConflict(
                formData.questionnaireResponses,
                formData._questionnaireResponseIds,
                loadedTyped._questionnaireResponseIds,
              )
            ) {
              throw new Error(
                "This Scribe interaction already contains an assessment being edited in this form. Refresh and review the recorded assessment before submitting.",
              );
            }
            updatedOriginalFormData = {
              ...originalFormData,
              ...loadedTyped,
              _clinicalImpressionIds: loadedTyped._clinicalImpressionIds || {},
              _questionnaireResponseIds:
                loadedTyped._questionnaireResponseIds || {},
              _observationIds: loadedTyped._observationIds || {},
            } as IntakeInteractionFormData;
            setOriginalFormData?.(updatedOriginalFormData);
          } catch (error) {
            console.error("Failed to load Scribe encounter");
            if (
              error instanceof Error &&
              error.message.startsWith("This Scribe interaction")
            ) {
              throw error;
            }
            throw new Error(
              "Could not load the Scribe interaction. Please refresh and try again.",
            );
          }
        }

        // Provenance stamp is create-only: a scribe-loaded or existing
        // encounter keeps whatever stamp it has (absence stays meaningful —
        // system-generated/legacy per the mixed-era contract).
        const flat = {
          ...toBundleInput(intakeType, formData),
          ...(!encounterToUpdate && {
            _encounterOrigin: activityContext
              ? ("activity-completion" as const)
              : ("free-form" as const),
          }),
        };
        const originalFlat = updatedOriginalFormData
          ? toBundleInput(intakeType, updatedOriginalFormData)
          : undefined;

        const facade = createEncounterFacade(medplum);
        const encounter = await facade.upsert({
          formData: flat,
          patient,
          practitioner: profile,
          practitionerRole: myRole,
          existingEncounter: encounterToUpdate,
          originalFormData: originalFlat,
          onWarning: (msg) => showWarningNotification(msg),
        });

        // Reconcile the Wellness/90791 intake ChargeItem to the saved billing
        // reality (create/update for Wellness, delete for CoCM/Unbilled). Keyed
        // idempotently by encounter id; self-heals on re-save if this fails.
        try {
          await reconcileIntakeChargeItem(medplum, {
            encounter,
            patient,
            practitionerRole: myRole,
          });
        } catch {
          // Do not log the raw error — a patient-scoped Medplum error can carry
          // resource identifiers (and console errors may forward to RUM).
          console.error("Failed to reconcile intake charge item");
          showWarningNotification(
            "Encounter saved, but the intake charge could not be recorded. It will be retried on the next save.",
          );
        }

        if (
          activityContext &&
          (!encounterToUpdate || encounterToUpdate.status === "in-progress")
        ) {
          try {
            const outcome = await intakeChain.run({
              medplum,
              encounter,
              formData,
              patient,
              practitionerRole: myRole,
              activityContext,
            });
            if (outcome.status === "success") {
              showSuccessNotification(outcome.message);
            } else {
              showWarningNotification(outcome.message);
            }
          } catch (activityError) {
            console.error(
              "Activity completion failed after encounter save:",
              activityError,
            );
            showWarningNotification(
              "Encounter saved successfully, but activity completion failed. Please complete the task manually.",
            );
          }
        } else {
          showSuccessNotification(
            `Encounter ${existingEncounter ? "updated" : "created"} successfully. All clinical data has been saved.`,
          );
        }

        onSubmitted?.();
        discardDraft?.();

        if (onSuccess && encounter.id) {
          onSuccess(encounter.id);
        } else {
          navigate(`/Patient/${patient.id}/Encounter/${encounter.id}`)?.catch(
            (error) => {
              console.error("Failed to navigate after encounter save:", error);
            },
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        showErrorNotification(errorMessage);
        console.error("Encounter save failed:", error);
      }
    },
    [
      profile,
      patient,
      medplum,
      navigate,
      existingEncounter,
      originalFormData,
      setOriginalFormData,
      onSuccess,
      onSubmitted,
      discardDraft,
      practitionerRoles,
      activityContext,
      intakeChain,
    ],
  );

  const handleDraftSave = useCallback(
    async (formData: IntakeInteractionFormData) => {
      if (!profile || !patient) {
        showErrorNotification(
          "Missing required data. Please refresh the page and try again.",
        );
        return;
      }

      const intakeType = formData["encounter-type"];

      try {
        const myRole = (practitionerRoles ?? []).find(
          (role) =>
            role.practitioner?.reference === `Practitioner/${profile.id}`,
        );

        // Draft creates carry the stamp too; re-saving an existing draft
        // preserves the original one (create-only rule).
        const flat = {
          ...toBundleInput(intakeType, formData),
          ...(!existingEncounter && {
            _encounterOrigin: activityContext
              ? ("activity-completion" as const)
              : ("free-form" as const),
          }),
        };
        const originalFlat = originalFormData
          ? toBundleInput(intakeType, originalFormData)
          : undefined;
        const draftFormState = buildDraftFormStateRecord(flat);

        const facade = createEncounterFacade(medplum);
        await facade.upsert({
          formData: flat,
          patient,
          practitioner: profile,
          practitionerRole: myRole,
          existingEncounter,
          originalFormData: originalFlat,
          onWarning: (msg) => showWarningNotification(msg),
          isDraft: true,
          draftFormState,
        });

        onSubmitted?.();
        discardDraft?.();
        showSuccessNotification("Draft saved successfully.");

        if (onSuccess) {
          onSuccess("");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        showErrorNotification(`Failed to save draft: ${errorMessage}`);
        console.error("Draft save failed:", error);
      }
    },
    [
      profile,
      patient,
      medplum,
      practitionerRoles,
      existingEncounter,
      originalFormData,
      onSubmitted,
      discardDraft,
      onSuccess,
    ],
  );

  return { handleSubmit, handleDraftSave };
};
